import { useMemo, useRef, useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  RefreshCw,
  FolderSync,
  FileWarning,
  Upload,
  KeyRound,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Modal } from '@/components/ui/modal';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/stores/toast-store';
import { formatDateTime } from '@/lib/utils';
import { useAdminTenants } from '@/hooks/use-admin';
import { useTenantLocationsForAutoPo } from '@/hooks/use-admin-auto-po';
import {
  useCcFundingMerchantMap,
  useCreateCcFundingMerchantMap,
  useUpdateCcFundingMerchantMap,
  useDeleteCcFundingMerchantMap,
  useCcFundingDriveSyncLog,
  useTriggerCcFundingDriveSync,
  useUploadCcFundingDriveFile,
  useCcFundingDriveCredentialsStatus,
  useSaveCcFundingDriveCredentials,
  useClearCcFundingDriveCredentials,
  type CcFundingMerchantMapEntry,
  type CcFundingDriveSyncStatus,
} from '@/hooks/use-admin-cc-funding-drive-sync';

// ── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<CcFundingDriveSyncStatus, 'success' | 'warning' | 'destructive' | 'default'> = {
  ingested: 'success',
  duplicate: 'default',
  unmapped: 'warning',
  error: 'destructive',
};

function statusLabel(status: CcFundingDriveSyncStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

// ── Create / Edit Modal ──────────────────────────────────────────────────────

interface MapFormState {
  tenantId: string;
  locationId: string;
  mid: string;
  merchantLabel: string;
  isActive: boolean;
}

const EMPTY_FORM: MapFormState = { tenantId: '', locationId: '', mid: '', merchantLabel: '', isActive: true };

function entryToForm(entry: CcFundingMerchantMapEntry): MapFormState {
  return {
    tenantId: entry.tenantId,
    locationId: entry.locationId,
    mid: entry.mid,
    merchantLabel: entry.merchantLabel ?? '',
    isActive: entry.isActive,
  };
}

function MerchantMapFormModal({
  open,
  onClose,
  editEntry,
}: {
  open: boolean;
  onClose: () => void;
  editEntry?: CcFundingMerchantMapEntry | null;
}) {
  const isEdit = !!editEntry;
  const [form, setForm] = useState<MapFormState>(editEntry ? entryToForm(editEntry) : EMPTY_FORM);

  const createMutation = useCreateCcFundingMerchantMap();
  const updateMutation = useUpdateCcFundingMerchantMap();
  const saving = createMutation.isPending || updateMutation.isPending;

  const { data: tenantsData } = useAdminTenants({ perPage: 200, isActive: 'true' });
  const tenants: any[] = tenantsData?.data ?? [];
  const { data: locationsData, isLoading: locationsLoading } = useTenantLocationsForAutoPo(form.tenantId);
  const locations = locationsData?.data ?? [];

  const tenantOptions = useMemo(() => tenants.map((t: any) => ({ value: t.id, label: t.name })), [tenants]);
  // Multiple locations under one tenant can share a generic/similar name
  // (e.g. QA seed data), making them indistinguishable in the dropdown —
  // appending the onboarding rep's email (when present) disambiguates them.
  const locationOptions = useMemo(
    () => locations.map((l) => ({ value: l.id, label: l.repEmail ? `${l.name} — ${l.repEmail}` : l.name })),
    [locations],
  );

  function update<K extends keyof MapFormState>(key: K, value: MapFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleTenantChange(tenantId: string) {
    setForm((prev) => ({ ...prev, tenantId, locationId: '' }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!form.tenantId) return toast.error('Tenant is required');
    if (!form.locationId) return toast.error('Location is required');
    if (!form.mid.trim()) return toast.error('MID is required');

    try {
      if (isEdit && editEntry) {
        await updateMutation.mutateAsync({
          id: editEntry.id,
          locationId: form.locationId,
          mid: form.mid,
          merchantLabel: form.merchantLabel.trim() || null,
          isActive: form.isActive,
        });
        toast.success('Mapping updated');
      } else {
        await createMutation.mutateAsync({
          tenantId: form.tenantId,
          locationId: form.locationId,
          mid: form.mid,
          merchantLabel: form.merchantLabel.trim() || undefined,
        });
        toast.success('Mapping created');
      }
      onClose();
    } catch {
      // Global mutation error handler shows toast
    }
  }

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Merchant Mapping' : 'Create Merchant Mapping'}
      widthClass="max-w-[560px]"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-9 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit as any}
            disabled={saving}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Mapping'}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Tenant</label>
          <SearchableSelect
            options={tenantOptions}
            value={form.tenantId}
            onChange={handleTenantChange}
            placeholder="Select a tenant..."
            searchPlaceholder="Search tenants..."
            disabled={isEdit}
            clearable={false}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Location</label>
          <SearchableSelect
            options={locationOptions}
            value={form.locationId}
            onChange={(v) => update('locationId', v)}
            placeholder={
              form.tenantId ? (locationsLoading ? 'Loading...' : 'Select a location...') : 'Select a tenant first'
            }
            searchPlaceholder="Search locations..."
            disabled={!form.tenantId || locationsLoading}
            clearable={false}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">MID</label>
          <input
            type="text"
            value={form.mid}
            onChange={(e) => update('mid', e.target.value)}
            placeholder='e.g. "266485490889" — the MID column value inside the funded file'
            className="h-9 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-muted-foreground font-mono"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            The value in the file's own "MID" column — not the filename. A single funded file can contain rows for
            several MIDs (different locations, even different tenants), so each MID needs its own mapping.
          </p>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Label (optional)</label>
          <input
            type="text"
            value={form.merchantLabel}
            onChange={(e) => update('merchantLabel', e.target.value)}
            placeholder='e.g. "VIJETHA Swagruha Foods" — for your own reference'
            className="h-9 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            Not used for matching — just a note to help you tell mappings apart in the table below.
          </p>
        </div>

        {isEdit && (
          <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
            <div>
              <p className="text-sm font-medium text-foreground">Active</p>
              <p className="text-xs text-muted-foreground">Inactive mappings are skipped by the Drive sync job</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.isActive}
              onClick={() => update('isActive', !form.isActive)}
              className={[
                'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                form.isActive ? 'bg-primary' : 'bg-gray-200',
              ].join(' ')}
            >
              <span
                className={[
                  'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm ring-0 transition-transform',
                  form.isActive ? 'translate-x-5' : 'translate-x-0',
                ].join(' ')}
              />
            </button>
          </div>
        )}
      </form>
    </Modal>
  );
}

// ── Google Drive Credentials Panel ──────────────────────────────────────────

const SOURCE_LABEL: Record<string, string> = {
  database: 'Uploaded key (database)',
  environment: 'Environment variable',
  none: 'Not configured',
};

function CredentialsPanel() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<{ name: string; contents: string } | null>(null);
  const [folderId, setFolderId] = useState('');

  const { data, isLoading } = useCcFundingDriveCredentialsStatus();
  const status = data?.data;
  const saveMutation = useSaveCcFundingDriveCredentials();
  const clearMutation = useClearCcFundingDriveCredentials();

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPendingFile({ name: file.name, contents: String(reader.result ?? '') });
    };
    reader.onerror = () => toast.error('Could not read the selected file');
    reader.readAsText(file);
  }

  async function handleSave() {
    if (!pendingFile) return;
    try {
      await saveMutation.mutateAsync({
        serviceAccountKeyJson: pendingFile.contents,
        folderId: folderId.trim() || undefined,
      });
      toast.success('Google Drive credentials saved');
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      // Global mutation error handler shows toast (e.g. invalid key file)
    }
  }

  async function handleClear() {
    try {
      await clearMutation.mutateAsync();
      toast.success('Uploaded key removed');
    } catch {
      // Global handler shows toast
    }
  }

  const configured = status?.configured ?? false;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground">Google Drive Credentials</h3>

      <div className="rounded-lg border border-border bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Service Account Key</span>
          </div>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Badge variant={configured ? 'success' : 'warning'}>
              <span className="inline-flex items-center gap-1">
                {configured ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                {SOURCE_LABEL[status?.source ?? 'none']}
              </span>
            </Badge>
          )}
        </div>

        {!configured && (
          <p className="text-xs text-amber-600">
            No credentials configured yet — the Drive sync (scheduled or "Sync Now") will fail until a service account
            key is uploaded here.
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Upload Key File (.json)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              onChange={handleFileChange}
              className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border file:border-border file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-foreground hover:file:bg-muted"
            />
            {pendingFile && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                Selected: <span className="font-mono">{pendingFile.name}</span> — not saved yet.
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Drive Folder ID (optional)</label>
            <input
              type="text"
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              placeholder={status?.folderId ? `Currently: ${status.folderId}` : 'Uses server default'}
              className="h-9 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
            />
            <p className="mt-1.5 text-xs text-muted-foreground">Leave blank to keep the current folder.</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={!pendingFile || saveMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Save Credentials
          </button>
          {status?.source === 'database' && (
            <button
              type="button"
              onClick={handleClear}
              disabled={clearMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              {clearMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Remove Uploaded Key
            </button>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          The uploaded key is stored securely and never displayed again after saving. Create it in Google Cloud Console
          (a service account with Drive API access), share the funded-files folder with its email address, then upload
          the downloaded JSON key here.
        </p>
      </div>
    </div>
  );
}

// ── Manual Upload Panel ──────────────────────────────────────────────────────

function ManualUploadPanel() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadCcFundingDriveFile();

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    uploadMutation.mutate(file, {
      onSuccess: (res) => {
        const ingested = res.midGroups.filter((g) => g.mapped && !g.duplicate).length;
        const duplicate = res.midGroups.filter((g) => g.mapped && g.duplicate).length;
        const unmapped = res.midGroups.filter((g) => !g.mapped);

        const parts = [`${res.midGroups.length} MID${res.midGroups.length === 1 ? '' : 's'} found`];
        if (ingested > 0) parts.push(`${ingested} ingested`);
        if (duplicate > 0) parts.push(`${duplicate} duplicate`);
        if (unmapped.length > 0) parts.push(`${unmapped.length} unmapped (${unmapped.map((g) => g.mid).join(', ')})`);

        if (unmapped.length > 0) toast.error(`"${res.fileName}" — ${parts.join(', ')}`);
        else toast.success(`"${res.fileName}" — ${parts.join(', ')}`);

        if (fileInputRef.current) fileInputRef.current.value = '';
      },
      onError: () => {
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });
  }

  return (
    <div className="space-y-3">
      <h3>
        <span className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white">
          Manual Upload
        </span>
      </h3>

      <div className="rounded-lg border border-border bg-white p-5 space-y-3">
        <p className="text-xs text-muted-foreground">
          Upload a funded-file workbook directly — no Google Drive access needed. Tenant/location for each row is
          resolved from the file's own MID column via the mappings below — a file can contain multiple MIDs, and any MID
          without a mapping yet is reported without blocking the rest of the file.
        </p>
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
            disabled={uploadMutation.isPending}
            className="block w-full max-w-sm text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border file:border-border file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-foreground hover:file:bg-muted disabled:opacity-50"
          />
          {uploadMutation.isPending && (
            <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading and ingesting...
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export function AdminCcFundingDriveSyncPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editEntry, setEditEntry] = useState<CcFundingMerchantMapEntry | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CcFundingMerchantMapEntry | null>(null);
  const [logPage, setLogPage] = useState(1);

  const { data: mapData, isLoading: mapLoading } = useCcFundingMerchantMap();
  const entries = mapData?.data ?? [];

  const { data: logData, isLoading: logLoading } = useCcFundingDriveSyncLog({ page: logPage, perPage: 20 });
  const logEntries = logData?.data ?? [];
  const logPagination = logData?.pagination;

  const deleteMutation = useDeleteCcFundingMerchantMap();
  const syncMutation = useTriggerCcFundingDriveSync();

  function handleCloseModal() {
    setShowCreateModal(false);
    setEditEntry(null);
  }

  async function handleDelete(entry: CcFundingMerchantMapEntry) {
    try {
      await deleteMutation.mutateAsync(entry.id);
      toast.success('Mapping deleted');
      setConfirmDelete(null);
    } catch {
      // Global handler shows toast
    }
  }

  function handleSyncNow() {
    syncMutation.mutate(undefined, {
      onSuccess: (res) => {
        const r = res.data;
        toast.success(
          `Sync complete — ${r.filesSeen} file${r.filesSeen === 1 ? '' : 's'} seen, ${r.ingested} ingested, ${r.duplicate} duplicate, ${r.unmapped} unmapped, ${r.errors} errors`,
        );
      },
    });
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="CC Funding — Drive Sync"
        description="Map incoming funded-file MIDs to a tenant/location, and monitor auto-ingestion from the shared Google Drive folder."
      >
        <button
          type="button"
          onClick={handleSyncNow}
          disabled={syncMutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
        >
          {syncMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Sync Now
        </button>
      </PageHeader>

      <CredentialsPanel />

      <ManualUploadPanel />

      {/* Merchant Map */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">MID Mappings</h3>
          <button
            type="button"
            onClick={() => {
              setEditEntry(null);
              setShowCreateModal(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Mapping
          </button>
        </div>

        {mapLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span className="text-sm">Loading mappings...</span>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-white overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-gray-50">
                <tr className="border-b border-border">
                  <th className="px-4 py-3 text-left text-[13px] font-semibold tracking-wide text-gray-700">MID</th>
                  <th className="px-4 py-3 text-left text-[13px] font-semibold tracking-wide text-gray-700">Label</th>
                  <th className="px-4 py-3 text-left text-[13px] font-semibold tracking-wide text-gray-700">Tenant</th>
                  <th className="px-4 py-3 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-[13px] font-semibold tracking-wide text-gray-700">Active</th>
                  <th className="px-4 py-3 text-left text-[13px] font-semibold tracking-wide text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <FolderSync className="h-9 w-9 text-muted-foreground/40" />
                        <p className="text-sm font-medium text-muted-foreground">No MID mappings yet</p>
                        <p className="text-xs text-muted-foreground">
                          Rows whose MID has no mapping show up as "Unmapped" in the sync log below.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  entries.map((entry) => (
                    <tr
                      key={entry.id}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-mono font-medium text-foreground">{entry.mid}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{entry.merchantLabel ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {entry.tenantName ?? entry.tenantId.slice(0, 8)}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {entry.locationName ?? entry.locationId.slice(0, 8)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={entry.isActive ? 'success' : 'default'}>
                          {entry.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => setEditEntry(entry)}
                            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                          >
                            <Pencil className="h-3 w-3" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(entry)}
                            className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sync Log */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Recent Sync Activity</h3>

        {logLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span className="text-sm">Loading sync log...</span>
          </div>
        ) : logEntries.length === 0 ? (
          <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">
            No files processed yet
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-border bg-white overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-50">
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left text-[13px] font-semibold tracking-wide text-gray-700">File</th>
                    <th className="px-4 py-3 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                      Processed
                    </th>
                    <th className="px-4 py-3 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                      Detail
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {logEntries.map((entry) => (
                    <tr
                      key={entry.id}
                      className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                    >
                      <td className="px-4 py-2.5 text-sm font-mono text-foreground">{entry.fileName}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant={STATUS_VARIANT[entry.status]}>{statusLabel(entry.status)}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTime(entry.processedAt)}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[360px] truncate">
                        {entry.status === 'unmapped' || entry.status === 'error' ? (
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <FileWarning className="h-3 w-3 shrink-0" />
                            {entry.message ?? '—'}
                          </span>
                        ) : (
                          (entry.message ?? '—')
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {logPagination && logPagination.totalPages > 1 && (
              <DataTablePagination
                page={logPagination.page}
                totalPages={logPagination.totalPages}
                total={logPagination.total}
                perPage={logPagination.perPage}
                onPageChange={setLogPage}
              />
            )}
          </>
        )}
      </div>

      {(showCreateModal || editEntry) && (
        <MerchantMapFormModal
          key={editEntry?.id ?? 'create'}
          open={showCreateModal || !!editEntry}
          onClose={handleCloseModal}
          editEntry={editEntry}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Mapping"
        message={`Are you sure you want to delete the mapping for MID "${confirmDelete?.mid ?? ''}"? Rows with this MID will show as "Unmapped" until re-mapped.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (confirmDelete) handleDelete(confirmDelete);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
